/**
 * createApp: bootstrap a PlayCanvas Application.
 *
 * Adapted from supersplat-viewer/src/index.ts (createApp / initCanvas), simplified:
 * WebGPU-preferred graphics device with WebGL2 fallback, standard Application
 * (auto-registers GSplat system/handler), camera rig (PlayerRoot + Camera child),
 * directional light, fixed canvas sizing.
 */

import {
    Application,
    Color,
    DEVICETYPE_WEBGPU,
    Entity,
    GSPLAT_RENDERER_RASTER_GPU_SORT,
    RESOLUTION_FIXED,
    createGraphicsDevice
} from 'playcanvas';
import { eventBus } from '../events/EventBus';
import { keyboardSheetActive } from '../ui/MobileKeyboardSheet';
import { initLayers } from '../render/LayerManager';
import { markStableSession, STABLE_SESSION_MS, mobileTierLevel, mobileDegradeActive } from './stabilityGuard';

export interface AppContext {
    app: Application;
    /** PlayerRoot entity — holds the player position. */
    cameraRoot: Entity;
    /** Camera entity (child of cameraRoot) — holds the camera rotation. */
    camera: Entity;
}

interface ViewportSize {
    width: number;
    height: number;
}

const MOBILE_CANVAS_PIXEL_RATIO = 2.0;
const MOBILE_CANVAS_PIXEL_RATIO_MIN = 1.6;
const MOBILE_CANVAS_PIXEL_RATIO_MAX = 2.25;
const MOBILE_CANVAS_PIXEL_RATIO_STEP_DOWN = 0.05;
const MOBILE_CANVAS_PIXEL_RATIO_STEP_UP = 0.05;
const MOBILE_DYNAMIC_SAMPLE_SECONDS = 4;
const MOBILE_DYNAMIC_LOW_FPS = 23;
const MOBILE_DYNAMIC_HIGH_FPS = 42;
// Mobile splat budget. 1.1M (the old value) is a static greedy allocation, not
// FPS-adaptive: with mainhall's LOD2-allowed finest at 11.4M splats, the
// balancer pinned the whole scene at LOD4-6 (probe-measured: engine held
// desires on LOD5/LOD6 only), i.e. ~5-10% density everywhere — the main cause
// of mobile blur after entry. 3M lets near-field nodes reach LOD2/LOD3 while
// still bounding rendered splats for mid-range phones; tune with probe-lod.
// 2026-09-22 真机帧率修复：3M → 1.2M，配合渲染上限 LOD2（loadGaussianScene
// 的 MOBILE_SHARED_HIGH_LOD_INDEX 1→2，此前近场精化到 LOD1≈桌面级画质，
// 手机帧率大跌）。1.2M 让 LOD2 只在镜头几米内、中距 LOD3/4、远处压最粗，
// 整条距离曲线比桌面（12M/LOD0）低约两档，渲染 splat 量比 3M 时期再降
// ~60%；minPixelSize 1→2 / minContribution 1.5→3（对齐桌面）额外裁掉
// 远处过小贡献的粒子 —— “太远也渲染高清”即由此消除。
// 2026-09-22 晚：1.2M → 2M。真机反馈手机端看不到桌面那种「近细远粗 + 朝向
// 切换」的梯度——1.2M 被脚下 LOD1 环吃光后，其余视场的 optimal 直接塌到
// 粗档，转身换面都是同一片粗，朝向变化无从感知（桌面 12M 买出整条距离曲线
// LOD0≤27m/LOD1≤90m/LOD2≤147m 才有梯度可看）。2M 让 LOD2/LOD3 中间档
// 重新进入平衡器分配范围，配合分环预载把对应档位数据铺到位；LOD0 仍被
// lodRangeMin 禁用。帧率兜底：动态像素比 <23FPS 自动降分辨率；若真机仍
// 明显掉帧，优先回调此值（1.6M/1.4M），不动上限与环表。
const MOBILE_GSPLAT_SPLAT_BUDGET = 2000000;
/** tier 1（单次崩溃恢复，stabilityGuard）的预算与 LOD 上限组合：2M→1.2M +
 *  LOD 上限 2，即 2026-09-22 真机帧率修复实测过的组合（见上沿革）——「被系统
 *  杀掉重启自动降一级」的温和档。 */
const MOBILE_GSPLAT_TIER1_SPLAT_BUDGET = 1200000;
/** tier 2（全面降档，stabilityGuard）：2M→600k，配合 LOD 上限收紧
 *  （loadGaussianScene）把流式解码与 VRAM 峰值一起压下来——2026-09-28 iPhone
 *  页面死亡循环的直接对症档。600k≈2M 的 30%，实测档位曲线（createApp 顶注）
 *  上近场仍保有 LOD3-4，可玩但明显粗。 */
const MOBILE_GSPLAT_DEGRADED_SPLAT_BUDGET = 600000;
// Desktop splat budget. These scenes need 14.07M (laomendong2) to 28.43M
// (mainhall / meihuashan) splats for full detail, so the engine's 1M default —
// what `splatBudget = 0` resolves to — starved even the near field.
//
// Measured on laomendong2 (furthest distance at which each LOD is still reached,
// camera at the spawn; the scene is 147m across):
//     budget  1M → LOD3 only within 1.7m, nothing finer anywhere
//     budget  8M → LOD0 ≤ 6.3m,  LOD1 ≤ 20.3m, LOD2 ≤ 61.4m
//     budget 12M → LOD0 ≤ 26.6m, LOD1 ≤ 90.0m, LOD2 ≤ 147m
//     budget 14.1M+ → whole scene pinned at LOD0 (its 14,065,780 total)
//
// 2026-09-19: 28.5M pinned mainhall at LOD0 everywhere (28.43M splats/frame
// → low desktop FPS; F8 LOD colorize solid red, no distance falloff at all).
// The balancer treats the budget as a TARGET: when the distance-curve total
// is below budget it refines nodes near→far toward LOD0 until filled, so an
// oversized budget lets the LOD0 fill creep across the whole scene over time
// (F8: far field slowly turning red).
// 2026-09-20: 8M → 12M. At 8M high detail stopped too close (laomendong2:
// LOD0 ≤ 6.3m / LOD1 ≤ 20.3m); probes run at a steady 60 FPS with headroom,
// so spend it on detail distance: 12M measured LOD0 ≤ 26.6m / LOD1 ≤ 90.0m /
// LOD2 ≤ 147m on laomendong2, VRAM commit ~420 MB (12M → capacity 13.8M →
// textureSize 3715). Still far below the 28.43M full-scene pin that broke FPS.
// 28.5M is the ceiling, not an arbitrary large number: it is just above the
// heaviest scene's full-detail count, so the balancer's `totalFinestCount <=
// budget` short-circuit pins *every* scene at LOD0 at every distance and no LOD
// degradation is possible anywhere. Raising it further is provably a no-op —
// the simulator renders byte-identical splat counts at 100M (laomendong2
// 14,065,780; mainhall 28,431,389) — while the work buffer keeps growing,
// because its render targets are sized from the allocator's *capacity*, not
// from actual use: GSplatWorldState.applyAllocations does
// `textureSize = ceil(sqrt(allocator.capacity))` and the allocator is built as
// `BlockAllocator(ceil(budget * 1.15))` up front. So the budget is a VRAM
// commitment regardless of what gets drawn:
//      12M  → capacity 13.8M → textureSize 3715 → ~420 MB
//    28.5M  → capacity 32.8M → textureSize 5725 → ~1000 MB
//     100M  → capacity 115M  → textureSize 10724 → ~3500 MB (device loss risk)
const DESKTOP_GSPLAT_SPLAT_BUDGET = 12000000;
const MOBILE_GSPLAT_MIN_PIXEL_SIZE = 2;
const MOBILE_GSPLAT_MIN_CONTRIBUTION = 3;
// 2026-09-22 晚：0.5 → 0.8，配合「视线前方最高精度」策略。foveation 按屏幕
// 位置加权 LOD：注视中心细、周边/脚下粗，把 2M 预算的额度从周边挪给视线
// 方向（与分环预载的前方细/侧后粗一档、lodBehindPenalty 三者同向）。桌面
// 恒 0=关：屏幕大、周边也是有效视野，加权不划算；手机屏幕小周边余光不重要。
const MOBILE_GSPLAT_FOVEATION_STRENGTH = 0.8;
const MOBILE_GSPLAT_FOVEATION_CENTER = 0.42;

let mobileCanvasPixelRatio = MOBILE_CANVAS_PIXEL_RATIO;
/** 降级档像素比上限：动态分辨率的上限从 2.0 压到 1.0（CSS 像素 1:1），
 *  填充率与纹理带宽同步减半；下限仍由动态分辨率逻辑自行收缩。仅 tier 2
 *  （全面降档）启用；tier 1 只动 LOD/预算，分辨率交动态分辨率自适应。
 *  降档判定来自 stabilityGuard（boot 死亡信号 或 mobileLowSpec 重载场景）。 */
const mobilePixelRatioCap = () => (mobileDegradeActive() ? 1.0 : MOBILE_CANVAS_PIXEL_RATIO);
/** 分级预算（stabilityGuard tier）：0=2M / 1=1.2M / 2=600k。 */
const mobileSplatBudgetCap = () => {
    const level = mobileTierLevel();
    return level >= 2 ? MOBILE_GSPLAT_DEGRADED_SPLAT_BUDGET
        : level === 1 ? MOBILE_GSPLAT_TIER1_SPLAT_BUDGET
        : MOBILE_GSPLAT_SPLAT_BUDGET;
};
/** 供 applyMobileSceneTier 复用 resize/预算设置（createApp 后非空）。 */
let currentApp: Application | null = null;
let dynamicResolutionTime = 0;
let dynamicResolutionFrames = 0;
let mobileSceneReady = false;

export async function createApp(canvas: HTMLCanvasElement): Promise<AppContext> {
    // markBoot 已前移到 main() 第一行（早于任何 await，极速死亡循环也要计数）；
    // 此处只按判定结果收紧 boot 档位。
    if (mobileTierLevel() > 0) {
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        console.info(`[stability] tier boot: level=${mobileTierLevel()} splatBudget=${mobileSplatBudgetCap()} pixelRatioCap=${mobilePixelRatioCap()}`);
    }
    const device = await createGraphicsDevice(canvas, {
        deviceTypes: [DEVICETYPE_WEBGPU],
        antialias: false,
        depth: true,
        powerPreference: 'high-performance'
    });

    const app = new Application(canvas, {
        graphicsDevice: device
    });
    currentApp = app;

    // 代理输入条（虚拟横屏键盘适配）打开期间冻结重算：键盘收缩 visualViewport，
    // 按收缩后的宽高重排会把旋转布局整体挤扁；收起后 vv 恢复会再触发一次 resize 还原。
    const resize = () => { if (!keyboardSheetActive()) resizeCanvasForDevice(app); };
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    window.visualViewport?.addEventListener('resize', resize);
    app.on('update', (dt: number) => updateDynamicMobileResolution(app, dt));
    eventBus.on('SCENE_LOAD_START', (event) => {
        if (!isMobileRuntime()) return;
        if (event.progress !== 0) return;
        mobileSceneReady = false;
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        resizeCanvasForDevice(app);
        console.info(`[mobile] dynamic pixel ratio reset for scene load=${mobileCanvasPixelRatio.toFixed(2)}`);
    });
    eventBus.on('SCENE_READY', () => {
        if (!isMobileRuntime()) return;
        mobileSceneReady = true;
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
    });

    // Render layers: Splat / Occlusion / World / Avatar / Overlay (see LayerManager).
    const layers = initLayers(app);

    // Streamed-gsplat tuning for fast camera turns:
    //   - renderer: prefer WebGPU GPU-side sort. On WebGL2 this resolves back
    //     to CPU sort, so the app still starts on browsers without WebGPU.
    //   - radialSorting: reduces rotation-induced sort artifacts while looking
    //     around in place.
    //   - lodUnderfillLimit/cooldownTicks: keep the previous streamed LOD
    //     fallback behavior for non-resident chunks.
    //   - lodBehindPenalty/lodUpdateAngle: let geometry behind the camera drop
    //     to a coarser LOD so its splats are reclaimed for what is in front.
    //     Both platforms want this; it is set for desktop as well, matching the
    //     supersplat-viewer preset.
    const mobile = isMobileRuntime();
    app.scene.gsplat.renderer = GSPLAT_RENDERER_RASTER_GPU_SORT;
    app.scene.gsplat.radialSorting = true;
    //   - lodUnderfillLimit: how many levels coarser than optimal a node may
    //     keep DISPLAYING while streaming. 3 (was 2 since 2026-09-20): with the
    //     LOD3 entry preload (2026-09-21), spawn nodes run optimal=LOD0 under the
    //     12M curve, and selectDesiredLodIndex only considers resident LODs in
    //     [optimal, optimal+limit] — limit 2 excluded LOD3, so newly-visible
    //     nodes had NOTHING resident in range and stayed invisible (blank spawn
    //     surroundings). Limit 3 puts the preloaded LOD3 back in range; it also
    //     keeps the graded far LOD4 (30-60m band, optimal≈1) displayable.
    app.scene.gsplat.lodUnderfillLimit = mobile ? 10 : 3;
    app.scene.gsplat.splatBudget = mobile
        ? mobileSplatBudgetCap()
        : DESKTOP_GSPLAT_SPLAT_BUDGET;
    app.scene.gsplat.minPixelSize = mobile ? MOBILE_GSPLAT_MIN_PIXEL_SIZE : 2;
    app.scene.gsplat.minContribution = mobile ? MOBILE_GSPLAT_MIN_CONTRIBUTION : 3;
    app.scene.gsplat.foveationStrength = mobile ? MOBILE_GSPLAT_FOVEATION_STRENGTH : 0;
    app.scene.gsplat.foveationCenter = mobile ? MOBILE_GSPLAT_FOVEATION_CENTER : 0.3;
    app.scene.gsplat.lodUpdateAngle = mobile ? 60 : 90;
    app.scene.gsplat.lodBehindPenalty = 5;
    app.scene.gsplat.colorUpdateAngle = mobile ? 4 : 2;
    app.scene.gsplat.alphaClip = 1 / 255;
    app.scene.gsplat.cooldownTicks = 60000;
    console.info(`[render] device=${device.deviceType}, gsplatRenderer=${app.scene.gsplat.currentRenderer}`);

    // Camera rig: PlayerRoot (position) + Camera (rotation).
    const cameraRoot = new Entity('PlayerRoot');
    app.root.addChild(cameraRoot);

    const camera = new Entity('Camera');
    cameraRoot.addChild(camera);
    camera.addComponent('camera', {
        clearColor: new Color(0.02, 0.02, 0.02),
        fov: 75
    });
    // Render skybox, default World (gsplat), and our custom layers.
    camera.camera!.layers = layers.allIds;

    // Directional light (gaussian splats are unlit, but lights help any meshes).
    const light = new Entity('Light');
    light.setEulerAngles(45, 45, 0);
    light.addComponent('light', {
        type: 'directional',
        intensity: 1
    });
    app.root.addChild(light);
    // Illuminate the character (Avatar), custom meshes (World), and markers (Overlay); splats are unlit.
    light.light!.layers = [layers.avatarOpaque.id, layers.avatarTransparent.id, layers.world.id, layers.overlay.id];

    app.scene.ambientLight.set(0.4, 0.42, 0.5);

    app.start();

    // 会话稳定存活满 2 分钟即视为健康：清空 boot 计数，下次进入恢复标准画质
    // （本会话保持降级档不变，中途放宽会在半途二次抬高峰值）。
    setTimeout(() => markStableSession(), STABLE_SESSION_MS);

    return { app, cameraRoot, camera };
}

export function isMobileRuntime(): boolean {
    const viewport = getViewportSize();
    return window.matchMedia('(pointer: coarse)').matches || viewport.width < 760;
}

function getViewportSize(): ViewportSize {
    const visualViewport = window.visualViewport;
    return {
        width: Math.max(1, Math.round(visualViewport?.width ?? window.innerWidth)),
        height: Math.max(1, Math.round(visualViewport?.height ?? window.innerHeight))
    };
}

function resizeCanvasForDevice(app: Application): void {
    const mobile = isMobileRuntime();
    const viewport = getViewportSize();
    const virtualLandscape = mobile && viewport.height > viewport.width;
    const width = virtualLandscape ? viewport.height : viewport.width;
    const height = virtualLandscape ? viewport.width : viewport.height;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, mobile ? mobileCanvasPixelRatio : 2);
    const root = document.getElementById('app-root');
    root?.classList.toggle('mobile-runtime', mobile);
    root?.classList.toggle('mobile-virtual-landscape', virtualLandscape);
    root?.classList.toggle('mobile-native-landscape', mobile && !virtualLandscape);
    root?.style.setProperty('--app-width', `${width}px`);
    root?.style.setProperty('--app-height', `${height}px`);
    root?.style.setProperty('--render-scale', `${pixelRatio}`);
    root?.setAttribute('data-render-scale', pixelRatio.toFixed(2));
    root?.setAttribute('data-canvas-size', `${Math.floor(width * pixelRatio)}x${Math.floor(height * pixelRatio)}`);
    app.graphicsDevice.maxPixelRatio = 1;
    app.setCanvasResolution(RESOLUTION_FIXED, Math.floor(width * pixelRatio), Math.floor(height * pixelRatio));
    app.resizeCanvas(width, height);
}

/** 场景级性能档位（switchTo 在 manifest 已知、开始流送之前调用）：按
 *  stabilityGuard 当前降档级别重设 splat 预算与像素比。重载场景
 *  （manifest.mobileLowSpec，如 meihuashan 784MB 桌面级 LOD 树）手机端
 *  无条件 tier 2（600k/像素比 1）；普通场景按 boot 信号分级（tier 1 单次
 *  崩溃 1.2M，无信号 2M/2.0）——传送门往返轻重场景时档位跟随切换。桌面恒
 *  12M（无降档语义）。 */
export function applyMobileSceneTier(): void {
    if (!currentApp) return;
    const mobile = isMobileRuntime();
    const budget = mobile
        ? mobileSplatBudgetCap()
        : DESKTOP_GSPLAT_SPLAT_BUDGET;
    currentApp.scene.gsplat.splatBudget = budget;
    if (mobile) {
        mobileCanvasPixelRatio = Math.min(mobileCanvasPixelRatio, mobilePixelRatioCap());
        resizeCanvasForDevice(currentApp);
    }
    console.info(`[stability] scene tier: level=${mobileTierLevel()} splatBudget=${budget} pixelRatioCap=${mobilePixelRatioCap().toFixed(1)}`);
}

function updateDynamicMobileResolution(app: Application, dt: number): void {
    if (!isMobileRuntime()) {
        dynamicResolutionTime = 0;
        dynamicResolutionFrames = 0;
        return;
    }
    if (!mobileSceneReady) return;

    dynamicResolutionTime += dt;
    dynamicResolutionFrames++;
    if (dynamicResolutionTime < MOBILE_DYNAMIC_SAMPLE_SECONDS) return;

    const averageFps = dynamicResolutionFrames / dynamicResolutionTime;
    // 动态升降两侧都以 mobilePixelRatioCap 为界：降档（boot 死亡信号或
    // mobileLowSpec 重载场景）时上限 1.0，回升路径不得越过它把降档顶回去；
    // 下限同理取 min（1.6 与 1.0 中取小），否则低帧收缩会把 1.0 抬回 1.6。
    const ratioCap = mobilePixelRatioCap();
    const ratioFloor = Math.min(MOBILE_CANVAS_PIXEL_RATIO_MIN, ratioCap);
    let nextPixelRatio = mobileCanvasPixelRatio;
    if (averageFps < MOBILE_DYNAMIC_LOW_FPS) {
        nextPixelRatio = Math.max(ratioFloor, mobileCanvasPixelRatio - MOBILE_CANVAS_PIXEL_RATIO_STEP_DOWN);
    } else if (averageFps > MOBILE_DYNAMIC_HIGH_FPS) {
        nextPixelRatio = Math.min(Math.min(ratioCap, MOBILE_CANVAS_PIXEL_RATIO_MAX), mobileCanvasPixelRatio + MOBILE_CANVAS_PIXEL_RATIO_STEP_UP);
    }
    nextPixelRatio = Math.min(ratioCap, nextPixelRatio);

    dynamicResolutionTime = 0;
    dynamicResolutionFrames = 0;
    if (nextPixelRatio === mobileCanvasPixelRatio) return;
    mobileCanvasPixelRatio = nextPixelRatio;
    resizeCanvasForDevice(app);
    console.info(`[mobile] dynamic pixel ratio=${mobileCanvasPixelRatio.toFixed(2)}, avgFps=${averageFps.toFixed(1)}`);
}
